import { Controller } from "@hotwired/stimulus"
import { loadStripe } from "../lib/stripe_loader"

// Settings › Donations, "Add a new card" (TMS-1888). Stripe's Payment Element takes the card in
// setup mode: nothing is charged. Add card turns it into a ConfirmationToken and posts it; the
// server saves it to the donor's cards (Payments::Stripe::AttachSavedCardService). When the bank
// asks for 3-D Secure, Stripe.js runs the check and the page posts again with the SetupIntent's id. Once
// saved, the page reopens the gift's Card panel with the new card ready to choose.
export default class extends Controller {
  static targets = ["card", "name", "submit", "error"]
  static values = { publishableKey: String, url: String, defaultName: String }

  connect() {
    this.onShow = this.show.bind(this)
    this.element.addEventListener("show.bs.modal", this.onShow)
  }

  disconnect() {
    this.element.removeEventListener("show.bs.modal", this.onShow)
  }

  // The gift whose Card panel opened the dialog, to reopen it afterwards. Stripe loads the first
  // time the dialog opens, not with the page.
  async show(event) {
    this.donationId = event.relatedTarget?.dataset?.donationId
    this.showError("")
    if (this.elements || !this.publishableKeyValue) return

    try {
      this.stripe = await loadStripe(this.publishableKeyValue)
      this.elements = this.stripe.elements({
        mode: "setup",
        currency: "usd",
        paymentMethodTypes: ["card"],
        appearance: { variables: { colorText: "#1A1C43", colorDanger: "#EF4444" } }
      })
      this.elements.create("payment", { fields: { billingDetails: { name: "never" } } }).mount(this.cardTarget)
    } catch (error) {
      console.error("Stripe initialization error:", error)
      this.showError("The card form didn't load. Please refresh the page and try again.")
    }
  }

  async submit(event) {
    event.preventDefault()
    if (!this.elements) return

    this.hold()
    this.showError("")
    try {
      const { error: submitError } = await this.elements.submit()
      if (submitError) throw submitError

      const name = this.nameTarget.value.trim() || this.defaultNameValue
      const { error, confirmationToken } = await this.stripe.createConfirmationToken({
        elements: this.elements,
        params: { payment_method_data: { billing_details: { name } } }
      })
      if (error) throw error

      let result = await this.post({ confirmation_token: confirmationToken.id })
      if (result.status === "authenticate") {
        const { error: actionError } = await this.stripe.handleNextAction({ clientSecret: result.client_secret })
        if (actionError) throw actionError
        result = await this.post({ setup_intent_id: result.setup_intent_id })
      }
      if (result.status !== "saved") throw new Error(result.message || "Your card could not be saved. Please try again.")

      window.location.assign(result.redirect_url)
    } catch (error) {
      this.showError(error.message || "Your card could not be saved. Please try again.")
      this.release()
    }
  }

  async post(body) {
    const response = await fetch(this.urlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify({ ...body, donation_id: this.donationId })
    })
    return response.json().catch(() => ({ status: "error" }))
  }

  hold() {
    this.submitTarget.disabled = true
    this.submitTarget.dataset.label ||= this.submitTarget.textContent
    this.submitTarget.textContent = "Saving…"
  }

  release() {
    this.submitTarget.disabled = false
    if (this.submitTarget.dataset.label) this.submitTarget.textContent = this.submitTarget.dataset.label
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.hidden = !message
  }
}
